import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import Loader from "../components/Loader";
import { useResendEmailVerificationMutation } from "../slices/usersApiSlice";

const VerifyEmailNoticeScreen = () => {
  const [params] = useSearchParams();
  const initialEmail = useMemo(() => params.get("email") || "", [params]);
  const [email, setEmail] = useState(initialEmail);
  const [resendEmailVerification, { isLoading }] = useResendEmailVerificationMutation();

  const submitHandler = async (e) => {
    e.preventDefault();
    const normalized = String(email || "").trim().toLowerCase();

    if (!normalized || !/^\S+@\S+\.\S+$/.test(normalized)) {
      toast.error("Enter a valid email address");
      return;
    }

    try {
      const res = await resendEmailVerification(normalized).unwrap();
      toast.success(res?.message || "Verification email sent");
    } catch (err) {
      toast.error(err?.data?.message || err?.error || "Could not resend verification email");
    }
  };

  return (
    <section className="mx-auto w-full max-w-xl">
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm md:p-8">
        <p className="mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-sky-700">
          Account
        </p>
        <h1 className="mb-3 text-3xl font-semibold text-slate-900">Verify your email</h1>
        <p className="mb-4 text-sm text-slate-600">
          We sent a verification link to your inbox. Open it to activate your account before signing in.
        </p>
        <div className="space-y-3 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm leading-6 text-slate-600">
          {initialEmail ? (
            <p className="m-0">
              The verification email was sent to <strong className="text-slate-800">{initialEmail}</strong>.
            </p>
          ) : null}
          <p className="m-0">
            If you do not see it right away, check your spam, junk, or promotions folders before trying again.
          </p>
          <p className="m-0">
            Verification links can expire. If that happens, you can request a new verification email below.
          </p>
        </div>

        <form onSubmit={submitHandler} className="mt-5 space-y-3">
          <label htmlFor="verificationEmail" className="block text-sm font-medium text-slate-700">
            Resend verification email
          </label>
          <input
            id="verificationEmail"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter email"
            className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-800 outline-none transition focus:border-sky-300 focus:bg-white focus:ring-2 focus:ring-sky-100"
          />
          <button
            type="submit"
            disabled={isLoading}
            className="inline-flex items-center justify-center rounded-xl bg-sky-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-sky-700 disabled:cursor-not-allowed disabled:bg-slate-300"
          >
            Resend verification
          </button>
          {isLoading ? <Loader /> : null}
        </form>
      </div>
    </section>
  );
};

export default VerifyEmailNoticeScreen;
